import React, { useCallback, useEffect, useState } from "react";
import { Button, Loading, Modal } from "@alm/flow-ds/components";
import QueryByForm from "../Finder/QueryByForm";
import { ViewDefinition } from "lib/api/types";
import { useTranslation } from "react-i18next";
import {
	Actions,
	clearSelected,
	FinderState,
	setSearchResults,
} from "../Finder/finderReducer";
import _ from "lodash";
import api from "lib/api";
import jsonpointer from "lib/jsonpointer/jsonpointer";
import { AxiosError } from "axios";
import { getErrorMessage } from "lib/api/errors";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import usePagedData from "hooks/usePagedData";
import { ListGroup, ListGroupItem } from "components/ListGroup";
import PaginationButton from "components/PaginationButton";
import ViewCard from "./ViewCard";
import ViewHeadings from "./ViewHeadings";
import { createAlertAction } from "data/app/appActions";
import { createAlert } from "records/Alert";
import { useDispatch } from "react-redux";

type Props = {
	viewDefinition: ViewDefinition | null;
	title: string;
	setViewDefinition: React.Dispatch<
		React.SetStateAction<ViewDefinition | null>
	>;
	finder: FinderState;
	dispatch: React.Dispatch<Actions>;
	setCursorLoading: React.Dispatch<React.SetStateAction<boolean>>;
	setError: React.Dispatch<React.SetStateAction<string | undefined>>;
	modalOpen: boolean;
	setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

function ViewFinder({
	viewDefinition,
	title,
	setViewDefinition,
	finder,
	dispatch,
	setCursorLoading,
	setError,
	modalOpen,
	setModalOpen,
}: Props) {
	const { t } = useTranslation();
	const errorDispatch = useDispatch();

	const [init, setInit] = useState(false);

	// Toggle between the list view and modify search
	const [modifyVisible, setModifyVisible] = useState(false);

	// Value for selected view
	const [checkedId, setCheckedId] = useState<string | undefined>(undefined);

	/**
	 * Handle record search
	 */
	const onSearchSubmit = useCallback(async () => {
		if (viewDefinition !== null) {
			// Set error to undefined
			setError(undefined);

			// Show the loader
			setCursorLoading(true);

			// Clear any selected records from previous searches
			dispatch(clearSelected());

			// Close the modal and set modify to true for when next opened from Modify button
			setModalOpen(false);
			setModifyVisible(true);

			// Create our query payload
			const payload = _.cloneDeep(
				viewDefinition.reference.search.payload
			);
			payload.filter.AND = [...payload.filter.AND, ...finder.query];
			payload.select = viewDefinition.reference.search.columns.map(c =>
				jsonpointer.pointerToId(c.id)
			);

			try {
				const resp = await api.cursors.create(payload);
				dispatch(setSearchResults(resp));
			} catch (err) {
				const e = err as AxiosError;
				setError(
					getErrorMessage(e) ||
						t("elements.finder.genericSearchErrorMsg")
				);
			} finally {
				setCursorLoading(false);
				setModalOpen(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		viewDefinition?.reference.search.columns,
		viewDefinition?.reference.search.payload,
		setCursorLoading,
		dispatch,
		setModalOpen,
		finder.query,
		setError,
		t,
	]);

	const onCloseClick = useCallback(() => {
		setModalOpen(false);
	}, [setModalOpen]);

	// Pagination for view list
	const { state, setData, ready, loading, addPage } = usePagedData();

	/**
	 * Load view definition when a view is selected
	 */
	useEffect(() => {
		if (checkedId !== undefined && checkedId !== "") {
			// Load view definition from API
			api.view
				.getModifiedView(
					checkedId.substring(checkedId.lastIndexOf("/") + 1)
				)
				.then(results => {
					setViewDefinition(results.data.data);
				})
				.catch(error => {
					console.error(error);
					errorDispatch(
						createAlertAction(
							createAlert({
								title: t(
									"alerts.viewDefinitionLoadError.title"
								),
								status: "error",
								description: t(
									"alerts.viewDefinitionLoadError.description"
								),
								timeout: 10000,
							})
						)
					);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checkedId]);

	/**
	 * Load list of views on first load
	 */
	useEffect(() => {
		if (!init) {
			// Load views from API
			api.view
				.getViews()
				.then(results => {
					setData({
						...results,
					});
					ready();
				})
				.catch(error => {
					console.error(error);
					errorDispatch(
						createAlertAction(
							createAlert({
								title: t(
									"alerts.viewDefinitionListLoadError.title"
								),
								status: "error",
								description: t(
									"alerts.viewDefinitionListLoadError.description"
								),
								timeout: 10000,
							})
						)
					);
				});
			setInit(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [init, ready, setData]);

	/**
	 * Add new views to page when paged
	 */
	const page = useCallback(async () => {
		addSentryBreadcrumb({
			category: "button.click",
			message: "Page view definitions",
		});

		loading();
		try {
			const res = await api.view.getViews({
				limit: state.limit,
				offset: state.offset,
			});
			addPage(res);
			ready();
		} catch (e) {
			console.error(e);
		}
	}, [addPage, loading, ready, state.limit, state.offset]);

	return (
		<>
			<Modal
				className={`finder__modal modal--fill-h results-visible--false view__modal`}
				modalSize="xl"
				isOpen={modalOpen}
				title={
					!modifyVisible
						? title
						: `Modify: ${viewDefinition?.viewLabel}`
				}
				footer={
					modifyVisible ? (
						<div className="finder__search-results-footer finder__search-results-footer--flex">
							<Button
								className="btn-basic--primary finder__back-btn"
								label={t("main.views.labels.back")}
								onClick={() => setModifyVisible(false)}
								icon="chevron-left"
							/>
							<Button
								type="submit"
								className="btn-primary"
								label={t("main.views.labels.showRecords")}
								onClick={onSearchSubmit}
							/>
						</div>
					) : (
						<div className="finder__search-results-footer">
							<Button
								disabled={_.isEmpty(viewDefinition)}
								className="btn-tertiary"
								label={t("main.views.labels.modify")}
								onClick={() => setModifyVisible(true)}
							/>
							<Button
								disabled={_.isEmpty(viewDefinition)}
								type="submit"
								className="btn-primary"
								label={t("main.views.labels.showRecords")}
								onClick={onSearchSubmit}
							/>
						</div>
					)
				}
				close={onCloseClick}
				closeAriaLabel={t("main.icons.closeModal")}>
				{!modifyVisible && state.loading && <Loading />}
				{!modifyVisible && init && (
					<>
						{state.results?.length ? (
							<>
								<ViewHeadings className="tasks__heading--views" />
								<ListGroup className="list-group-links">
									{state.results.map(
										(view, index: number) => (
											<div
												key={`item__${index}`}
												className="list-group-item-wrapper"
												onClick={() =>
													setCheckedId(view._id)
												}>
												<ListGroupItem
													className={`task-card task-card--claimed task-card--read view-modal-card ${
														view._id === checkedId
															? "table-active"
															: ""
													}`}
													key={`view__${index}`}>
													<ViewCard
														id={view._id}
														viewLabel={
															view.viewLabel
														}
														description={
															view.description
														}
														handleRadioClick={(
															id: string
														) => {
															setCheckedId(id);
														}}
														isChecked={
															view._id ===
															checkedId
														}
													/>
												</ListGroupItem>
											</div>
										)
									)}
								</ListGroup>
							</>
						) : (
							!state.loading && (
								<p className="no-views">
									{t("main.views.text.noViews")}
								</p>
							)
						)}
						{state.hasMore ? (
							<PaginationButton
								pageType="views"
								loading={state.loading}
								page={page}
								hasMore={state.hasMore}
							/>
						) : (
							<div className="mb-50"></div>
						)}
					</>
				)}
				<div
					style={{
						display: modifyVisible ? "block" : "none",
					}}
					className="finder__search-form">
					<QueryByForm
						activeSearchFields={finder.activeSearchFields}
						dispatch={dispatch}
						searchFields={finder.searchFields}
						querySummary={finder.querySummary}
						query={finder.query}
					/>
				</div>
			</Modal>
		</>
	);
}

export default ViewFinder;
